import {
  Avatar,
  Box,
  Paper,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Canvas from "./Canvas";
import Picker from "./Picker";

function PositionCard({ title, subtitle, slider }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: (theme) => theme.palette.surface?.elevated,
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        {slider}
      </Stack>
    </Paper>
  );
}

export default function PreviewWorkspace({
  canvasRef,
  characterIndex,
  currentCharacter,
  draw,
  fontSize,
  onCharacterChange,
  onHorizontalPositionChange,
  onVerticalPositionChange,
  position,
  verticalSliderValue,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: 3, sm: 4 },
        border: (themeContext) =>
          `1px solid ${themeContext.palette.outline?.main}`,
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              alt={currentCharacter.name}
              src={`/img/${currentCharacter.img}`}
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                border: (themeContext) =>
                  `1px solid ${themeContext.palette.outline?.accent}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">
                Preview / 预览工作台
              </Typography>
              <Typography variant="h6" noWrap>
                {currentCharacter.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Sticker #{currentCharacter.id} · 调整位置并即时查看效果
              </Typography>
            </Box>
          </Stack>

          <Picker
            value={characterIndex}
            onChange={onCharacterChange}
            accentColor={currentCharacter.color}
          />
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <Paper
            variant="outlined"
            sx={{
              flexGrow: 1,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 3,
              background: (themeContext) =>
                `linear-gradient(180deg, ${alpha(
                  themeContext.palette.common.white,
                  0.04
                )} 0%, ${themeContext.palette.surface?.elevated} 100%)`,
            }}
          >
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2.5,
                border: (themeContext) =>
                  `1px solid ${themeContext.palette.outline?.main}`,
                backgroundColor: alpha("#000000", 0.18),
              }}
            >
              <Canvas
                ref={canvasRef}
                draw={draw}
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  margin: "0 auto",
                  maxWidth: "100%",
                  borderRadius: "12px",
                }}
              />
            </Box>
          </Paper>

          {!isMobile && (
            <PositionCard
              title="Vertical / 垂直"
              subtitle="移动文字的上下位置"
              slider={
                <Box
                  sx={{
                    px: 1,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Slider
                    value={verticalSliderValue}
                    onChange={onVerticalPositionChange}
                    min={0}
                    max={256}
                    step={1}
                    orientation="vertical"
                    sx={{ height: 312 }}
                  />
                </Box>
              }
            />
          )}
        </Stack>

        <Stack spacing={2}>
          <PositionCard
            title="Horizontal / 水平"
            subtitle="左右移动文字，让标题更贴合贴纸构图"
            slider={
              <Slider
                value={position.x}
                onChange={onHorizontalPositionChange}
                min={0}
                max={296}
                step={1}
              />
            }
          />

          {isMobile && (
            <PositionCard
              title="Vertical / 垂直"
              subtitle="手机上改成横向滑杆，更容易单手微调"
              slider={
                <Slider
                  value={verticalSliderValue}
                  onChange={onVerticalPositionChange}
                  min={0}
                  max={256}
                  step={1}
                />
              }
            />
          )}
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: (themeContext) => themeContext.palette.tonal?.soft,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Current position: X {position.x}, Y {position.y}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sticker text color: {currentCharacter.color} · Font size {fontSize}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
